import {
  Text,
  Badge,
  GreyGrey100,
  GreyGrey60,
  GreyGrey70,
  GreyGrey90,
  IconClipboard,
  IndigoIndigo60,
  Link,
  Primary13Regular,
  Primary13SemiBold,
  Primary14SemiBold,
  Skeleton,
  useNotifications,
  Flex,
  Grid
} from '@sequencehq/core-components'
import capitalize from 'lodash/capitalize'
import kebabCase from 'lodash/kebabCase'
import { pricingTypeLabel } from 'modules/Products/formatters'
import {
  enforceMinimumPrecision,
  OldPriceModel,
  priceModelAdapter,
  toBillingFrequencyLabel,
  toBillingType,
  toMoney,
  toPriceSummary
} from '@sequencehq/utils'
import { ListPrice, Product } from 'modules/Products/types'
import usageCalculationPeriodMapper from 'common/drawers/PricingEditor/utils/usageCalculationPeriodMapper.ts'
import { usageCalculationPeriodLabels } from 'common/drawers/PricingEditor/view/editors/GraduatedPrice/GraduatedPriceForm.constants.ts'
import { toRevenueRecognitionMethodLabel } from 'modules/RevenueRecognition'
import { useQuery } from '@sequencehq/api/utils'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import { NEW_PRICE_PATTERN } from 'common/drawers/PricingEditor/domain/pricingEditor.constants'
import { useMemo } from 'react'
import { useSeatMetrics } from 'modules/Seats/SeatMetric/useSeatMetrics'

const PRICING_TYPES_WITH_PERCENTAGE = ['LINEAR', 'GRADUATED', 'VOLUME']

type SeatBasedProrationStrategy =
  | 'USE_MAXIMUM'
  | 'PRORATE_INCREMENTS'
  | 'PRORATE_ALL_CHANGES'
  | 'USE_FIRST'

// Human-readable labels for seat proration strategies
const seatProrationLabels: Record<SeatBasedProrationStrategy, string> = {
  USE_MAXIMUM: 'No proration (charge full amount)',
  PRORATE_INCREMENTS: 'Prorate added seats only',
  PRORATE_ALL_CHANGES: 'Prorate added and removed seats',
  USE_FIRST: 'Use first' // Fallback mapping
}
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'

const deriveIsPricePercentage = (
  structure?: OldPriceModel['structure'] | ListPrice['structure']
): boolean => {
  if (!structure) {
    return false
  }

  if (structure.pricingType === 'VOLUME') {
    return structure.tiers[0].isPricePercentage
  }

  if (structure.pricingType === 'GRADUATED') {
    return structure.tiers[0].isPricePercentage
  }

  if (structure.pricingType === 'LINEAR') {
    return structure.isPricePercentage
  }

  return false
}

const isSeatBasedGraduated = (
  structure?: OldPriceModel['structure'] | ListPrice['structure']
): boolean => {
  if (!structure || structure.pricingType !== 'SEAT_BASED') {
    return false
  }

  return !!structure.tiers && structure.tiers.length > 0
}

export const ProductPriceMeta = ({
  price,
  product
}: {
  price: OldPriceModel | ListPrice
  product?: Product
}) => {
  const { displayNotification } = useNotifications()

  const { data: taxCategoriesData } = useQuery(
    dashboardv99990101Client.getTaxCategories
  )

  const taxCategoryForProduct = taxCategoriesData?.items?.find(
    taxCategory => taxCategory.id === product?.taxCategoryId
  )

  const handleCopy = () => {
    void navigator.clipboard.writeText(price.id)
    displayNotification('Price ID copied', {
      type: 'success',
      isClosable: true,
      duration: 2000
    })
  }

  const handleProductCopy = () => {
    if (!product) return
    void navigator.clipboard.writeText(product.id)
    displayNotification('Product ID copied', {
      type: 'success',
      isClosable: true,
      duration: 2000
    })
  }

  const transformedPrice =
    'integrationIds' in price ? price : priceModelAdapter.out(price)

  return (
    <Flex flexDirection="column">
      <Text {...Primary14SemiBold} color={GreyGrey100}>
        Details
      </Text>
      <Grid
        templateColumns={`${product?.revenueRecognitionMethod ? '176px' : '160px'} 1fr`}
        gap={3}
        mt={4}
        color={GreyGrey70}
      >
        {product && (
          <>
            <Text {...Primary13Regular}>Product name</Text>
            <Text {...Primary13Regular} color={GreyGrey90}>
              {product.name}
            </Text>

            <Text {...Primary13Regular}>Product ID</Text>
            <Flex gap={2}>
              <Badge
                sentiment="neutral"
                containerStyle={{
                  height: '16px',
                  padding: 1
                }}
              >
                {product.id}
              </Badge>
              <IconClipboard
                data-testid="productPrice.copyProductId"
                size={14}
                color={GreyGrey60}
                onClick={handleProductCopy}
                cursor="pointer"
              />
            </Flex>

            {product.label && (
              <>
                <Text {...Primary13Regular}>Product description</Text>
                <Text
                  {...Primary13Regular}
                  color={GreyGrey90}
                  whiteSpace="normal"
                  wordBreak="break-word"
                >
                  {product.label}
                </Text>
              </>
            )}

            {product.revenueRecognitionMethod && (
              <>
                <Text {...Primary13Regular}>Revenue recognition method</Text>
                <Text
                  {...Primary13Regular}
                  color={GreyGrey90}
                  data-testid="pricingEditor.review.revenueRecognitionMethod"
                >
                  {toRevenueRecognitionMethodLabel(
                    product.revenueRecognitionMethod
                  )}
                </Text>
              </>
            )}

            {taxCategoryForProduct && (
              <>
                <Text {...Primary13Regular}>Tax category</Text>
                <Text
                  {...Primary13Regular}
                  color={GreyGrey90}
                  data-testid="pricingEditor.review.taxCategoryName"
                >
                  {taxCategoryForProduct.name}
                </Text>
              </>
            )}
          </>
        )}
        <Text {...Primary13Regular}>Pricing model</Text>
        <Text
          {...Primary13Regular}
          color={GreyGrey90}
          data-testid="pricingEditor.review.pricingModel"
        >
          {price.structure.pricingType === 'SEAT_BASED'
            ? `Seat-based → ${isSeatBasedGraduated(price.structure) ? 'Graduated' : 'Linear'} pricing`
            : capitalize(
                kebabCase(pricingTypeLabel(price.structure.pricingType))
              ) + ' pricing'}
        </Text>

        <Text {...Primary13Regular}>Billing frequency</Text>
        <Text
          {...Primary13Regular}
          color={GreyGrey90}
          data-testid="pricingEditor.review.billingFrequency"
        >
          {toBillingFrequencyLabel(price.billingFrequency)} /{' '}
          {toBillingType(price.billingType)}
        </Text>

        {price.structure.pricingType !== 'ONE_TIME' &&
          price.structure.pricingType !== 'FIXED' &&
          price.structure.pricingType !== 'SEAT_BASED' && (
            <PriceUsageMetric usageMetricId={price.structure.usageMetricId} />
          )}

        {price.structure.pricingType === 'SEAT_BASED' && (
          <PriceSeatMetric
            seatMetricId={
              'seatMetricId' in price.structure
                ? price.structure.seatMetricId
                : price.structure.seatTypeId
            }
          />
        )}

        {PRICING_TYPES_WITH_PERCENTAGE.includes(
          price.structure.pricingType
        ) && (
          <>
            <Text {...Primary13Regular}>Type</Text>
            <Text {...Primary13Regular} color={GreyGrey90}>
              {deriveIsPricePercentage(price.structure)
                ? 'Percentage'
                : 'Fixed'}
            </Text>
          </>
        )}

        {/* Seat-based pricing additional fields */}
        {price.structure.pricingType === 'SEAT_BASED' && (
          <>
            <Text {...Primary13Regular}>Minimum seats</Text>
            <Text {...Primary13Regular} color={GreyGrey90}>
              {'contractedMinimumSeats' in price.structure
                ? price.structure.contractedMinimumSeats
                : price.structure.includedSeats}
            </Text>

            <Text {...Primary13Regular}>Seat proration</Text>
            <Text {...Primary13Regular} color={GreyGrey90}>
              {price.structure.prorationStrategy in seatProrationLabels
                ? seatProrationLabels[
                    price.structure
                      .prorationStrategy as SeatBasedProrationStrategy
                  ]
                : capitalize(kebabCase(price.structure.prorationStrategy))}
            </Text>

            {'prorateFlatFees' in price.structure && (
              <>
                <Text {...Primary13Regular}>Flat fee proration</Text>
                <Text {...Primary13Regular} color={GreyGrey90}>
                  {price.structure.prorateFlatFees ? 'Enabled' : 'Disabled'}
                </Text>
              </>
            )}

            {'overagesBillingFrequency' in price.structure &&
              !['NEVER', 'NONE'].includes(
                price.structure.overagesBillingFrequency ?? ''
              ) && (
                <>
                  <Text {...Primary13Regular}>Overage frequency</Text>
                  <Text {...Primary13Regular} color={GreyGrey90}>
                    {toBillingFrequencyLabel(
                      price.structure.overagesBillingFrequency
                    )}
                  </Text>
                </>
              )}
          </>
        )}

        <GraduatedPricingFields price={price} />

        {price.structure.pricingType === 'LINEAR' ? (
          <LinearPricingFields price={price} />
        ) : price.structure.pricingType === 'PACKAGE' ? (
          <PackagePricingFields price={price} />
        ) : price.structure.pricingType === 'SEAT_BASED' &&
          !isSeatBasedGraduated(price.structure) ? (
          <>
            <Text {...Primary13Regular}>Price per seat</Text>
            <Text {...Primary13Regular} color={GreyGrey90}>
              {toMoney({
                currency: price.currency,
                value: price.structure.pricePerSeat,
                maximumFractionDigits: 10
              })}
            </Text>
          </>
        ) : (
          <>
            <Text {...Primary13Regular}>Price</Text>
            <Text
              {...Primary13Regular}
              color={GreyGrey90}
              data-testid="pricingEditor.review.price"
            >
              {toPriceSummary(transformedPrice)}
            </Text>
          </>
        )}

        {!price.id.match(NEW_PRICE_PATTERN) && (
          <>
            <Text {...Primary13Regular}>Price ID</Text>
            <Flex gap={2}>
              <Badge
                sentiment="neutral"
                containerStyle={{
                  height: '16px',
                  padding: 1
                }}
              >
                {price.id}
              </Badge>
              <IconClipboard
                data-testid="productPrice.copyPriceId"
                size={14}
                color={GreyGrey60}
                onClick={handleCopy}
                cursor="pointer"
              />
            </Flex>
          </>
        )}
      </Grid>
    </Flex>
  )
}

const PriceSeatMetric = ({ seatMetricId }: { seatMetricId: string }) => {
  const { metrics, loading } = useSeatMetrics()

  const seatMetric = useMemo(() => {
    if (!metrics) return null
    return metrics.find(metric => metric.id === seatMetricId)
  }, [metrics, seatMetricId])

  if (loading) {
    return (
      <>
        <Text {...Primary13Regular}>Seat metric</Text>
        <Skeleton width="100px" />
      </>
    )
  }

  if (!seatMetric) {
    return (
      <>
        <Text {...Primary13Regular}>Seat metric</Text>
        <Text {...Primary13Regular} color={GreyGrey90}>
          Not found
        </Text>
      </>
    )
  }

  // Link to the general seat metrics page
  const linkToSeatMetricsPage = `/seats`

  return (
    <>
      <Text {...Primary13Regular}>Seat metric</Text>
      <Link.Unstyled to={linkToSeatMetricsPage}>
        <Text {...Primary13SemiBold} color={IndigoIndigo60}>
          {seatMetric.label}
        </Text>
      </Link.Unstyled>
    </>
  )
}

const PriceUsageMetric = ({ usageMetricId }: { usageMetricId: string }) => {
  const usageMetricQuery = useQuery(dashboard20240730Client.getUsageMetric, {
    id: usageMetricId
  })

  const usageMetricValue = usageMetricQuery.data

  if (usageMetricQuery.isPending || !usageMetricValue) {
    return (
      <>
        <Text {...Primary13Regular}>Usage metric</Text>
        <Skeleton width="100px" />
      </>
    )
  }

  const linkToUsageMetric = `/usage-metrics/${usageMetricValue.id}`

  return (
    <>
      <Text {...Primary13Regular}>Usage metric</Text>
      <Link.Unstyled to={linkToUsageMetric}>
        <Text
          {...Primary13SemiBold}
          color={IndigoIndigo60}
          data-testid="pricingEditor.review.usageMetric"
        >
          {usageMetricValue.name}
        </Text>
      </Link.Unstyled>
    </>
  )
}

const LinearPricingFields = ({
  price
}: {
  price: OldPriceModel | ListPrice
}) => {
  const { structure, currency } = price

  if (structure.pricingType !== 'LINEAR') {
    return null
  }

  return (
    <>
      {structure.isPricePercentage ? (
        <>
          <Text {...Primary13Regular}>Percentage</Text>
          <Text {...Primary13Regular} color={GreyGrey90}>
            {enforceMinimumPrecision(2)(structure.pricePerUnit)}%
          </Text>
        </>
      ) : (
        <>
          <Text {...Primary13Regular}>Price per unit</Text>
          <Text {...Primary13Regular} color={GreyGrey90}>
            {toMoney({
              currency,
              value: structure.pricePerUnit,
              maximumFractionDigits: 10
            })}
          </Text>
        </>
      )}
      {structure.minPrice && (
        <>
          <Text {...Primary13Regular}>Min. Price</Text>
          <Text {...Primary13Regular} color={GreyGrey90}>
            {toMoney({
              currency,
              value: structure.minPrice,
              maximumFractionDigits: 10
            })}
          </Text>
        </>
      )}
      {structure.maxPrice && (
        <>
          <Text {...Primary13Regular}>Max. Price</Text>
          <Text {...Primary13Regular} color={GreyGrey90}>
            {toMoney({
              currency,
              value: structure.maxPrice,
              maximumFractionDigits: 10
            })}
          </Text>
        </>
      )}
    </>
  )
}

const PackagePricingFields = ({
  price
}: {
  price: OldPriceModel | ListPrice
}) => {
  const { structure, currency } = price

  if (structure.pricingType !== 'PACKAGE') {
    return null
  }

  return (
    <>
      {structure.pricePerPackage && (
        <>
          <Text {...Primary13Regular}>Price per package</Text>
          <Text {...Primary13Regular} color={GreyGrey90}>
            {toMoney({
              currency,
              value: structure.pricePerPackage,
              maximumFractionDigits: 10
            })}
          </Text>
        </>
      )}
      {structure.packageSize && (
        <>
          <Text {...Primary13Regular}>Package size</Text>
          <Text {...Primary13Regular} color={GreyGrey90}>
            {structure.packageSize}
          </Text>
        </>
      )}
    </>
  )
}

const GraduatedPricingFields = ({
  price
}: {
  price: ListPrice | OldPriceModel
}) => {
  const { structure } = price

  if (structure.pricingType !== 'GRADUATED') {
    return null
  }

  const usageCalculationPeriodValue =
    usageCalculationPeriodMapper.fromApiPricePropertiesToUsageCalculationPeriodDropdown(
      {
        usageCalculationPeriod:
          'usageCalculationPeriod' in price
            ? price.usageCalculationPeriod
            : undefined,
        usageCalculationMode: structure.usageCalculationMode
      },
      price.billingFrequency
    )

  return (
    <>
      <Text {...Primary13Regular}>Usage calculation period</Text>
      <Text {...Primary13Regular} color={GreyGrey90}>
        {usageCalculationPeriodLabels[usageCalculationPeriodValue]}
      </Text>
    </>
  )
}
